'use client';

import Header from '@/components/platform/Header';

import DynamicBody from '@/components/core/DynamicBody';
import { AppSupport, Footer } from '@/app/_components/platform';
import { FaqBanner, Faqs } from '@/app/components/shared';

import { ClientProvider } from '@/contexts/client.context';

import { products } from '@/utils/products';
import TriageCTA from '../platform/TriageCTA';
import { mergeObjects } from '@/utils/merge-objects';
import CoveredModal from '../core/CoveredModal';
import HeroWithCards from '@/components/platform/HeroWithCards';
import { useSearchParams } from 'next/navigation';

const PlatformClientPage = ({ client }) => {
  const {
    logos,
    name,
    clientKey,
    institutionKey,
    isActive,
    comingSoonHref,
    launchDate,
    countries,
    country,
    hasClientTriage,
    eligibilityClaim,
  } = client;

  function extractFieldsFromTemplate() {
    if (!client.template.data) {
      return null;
    }

    return client.template.data.attributes;
  }

  const template = extractFieldsFromTemplate();
  // override template variables
  const header = {
    ...mergeObjects(template?.header, client?.header),
    logos: logos?.data,
    isActive,
    comingSoonHref,
  };

  const headerConditions =
    !!client?.header?.conditions.data && client?.header?.conditions.data.length > 0
      ? client?.header?.conditions
      : template?.header?.conditions;

  const heroRolling = {
    ...mergeObjects(template?.heroRolling, client?.heroRolling !== undefined),
    logos: logos?.data,
    isActive,
    comingSoonHref,
  };

  const contact = mergeObjects(template?.contact, client?.contact);
  const faqBanner = mergeObjects(template?.faqBanner, client?.faqBanner);
  const disclaimer = client.disclaimer ?? template?.disclaimer;
  const faqs = mergeObjects(template?.faqs, client?.faqs);
  const triageCTA = mergeObjects(template?.triageCTA, client?.triageCTA);
  const footnotes = client?.footnotes?.length ? client.footnotes : template?.footnotes;

  const searchParams = useSearchParams();
  const paramsString = searchParams.toString();

  return (
    <>
      <ClientProvider
        defaultClient={{
          name,
          product: products.PLATFORM,
          clientKey,
          institutionKey,
          isActive,
          launchDate,
          countries,
          country,
          comingSoonHref: header.comingSoonHref,
          hasTriage: hasClientTriage,
        }}
      >
        <CoveredModal />
        {!heroRolling?.id && (
          <Header
            content={{ ...header, conditions: headerConditions }}
            countries={countries}
            product={products.PLATFORM}
            logos={logos?.data}
            showSwordLogo={client?.showSwordLogo}
            eligibilityClaim={eligibilityClaim}
          />
        )}

        {heroRolling.id !== undefined && (
          <HeroWithCards
            content={heroRolling}
            params={paramsString}
            showSwordLogo={client?.showSwordLogo}
            eligibilityClaim={eligibilityClaim}
          />
        )}

        {template?.body && (
          <DynamicBody body={template.body} isOrganicLP client={clientKey} scrollTop={false} />
        )}

        {faqs && <Faqs content={faqs} />}
        {faqBanner && <FaqBanner contact={contact} content={faqBanner} />}
        {hasClientTriage && triageCTA && <TriageCTA content={triageCTA} />}
        {template?.app && <AppSupport content={template.app} />}
        <Footer
          bottomCTA={template?.bottomCTA}
          disclaimer={disclaimer}
          footnotes={footnotes}
          email={contact?.email}
          isOrganicLP
        />
      </ClientProvider>
    </>
  );
};

export default PlatformClientPage;
